<template>
  <div class="subtitle-format-cards">
    <h2 class="section-title">{{ $t('subtitlePage.formatCards.title') }}</h2>

    <div class="format-grid">
      <div
        v-for="format in formats"
        :key="format.name"
        class="format-card"
      >
        <div class="card-header">
          <Icon :name="format.icon" size="32" class="format-icon" />
          <h3 class="format-name">{{ format.title }}</h3>
        </div>

        <div class="format-image">
          <img :src="format.image" :alt="format.alt" loading="lazy" />
        </div>

        <div class="format-details">
          <div class="detail-item">
            <span class="detail-label">{{ $t('subtitlePage.formatCards.labels.supported') }}:</span>
            <span class="detail-value">{{ format.extensions.join(', ') }}</span>
          </div>

          <div class="detail-item">
            <span class="detail-label">{{ $t('subtitlePage.formatCards.labels.preserve') }}:</span>
            <span class="detail-value">{{ format.features.join('、') }}</span>
          </div>

          <div class="detail-item">
            <span class="detail-label">{{ $t('subtitlePage.formatCards.labels.applyTo') }}:</span>
            <span class="detail-value">{{ format.useCases.join('、') }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
const formats = [
  {
    name: 'SRT',
    title: t('subtitlePage.formatCards.items.srt.title'),
    alt: t('subtitlePage.formatCards.items.srt.alt'),
    icon: 'material-symbols:closed-caption',
    image: '/images/subtitle/format-srt.jpeg',
    extensions: ['.srt'],
    features: [
      t('subtitlePage.formatCards.items.srt.features.0'),
      t('subtitlePage.formatCards.items.srt.features.1'),
      t('subtitlePage.formatCards.items.srt.features.2')
    ],
    useCases: [
      t('subtitlePage.formatCards.items.srt.useCases.0'),
      t('subtitlePage.formatCards.items.srt.useCases.1')
    ]
  },
  {
    name: 'VTT',
    title: t('subtitlePage.formatCards.items.vtt.title'),
    alt: t('subtitlePage.formatCards.items.vtt.alt'),
    icon: 'material-symbols:web',
    image: '/images/subtitle/format-vtt.jpeg',
    extensions: ['.vtt'],
    features: [
      t('subtitlePage.formatCards.items.vtt.features.0'),
      t('subtitlePage.formatCards.items.vtt.features.1'),
      t('subtitlePage.formatCards.items.vtt.features.2')
    ],
    useCases: [
      t('subtitlePage.formatCards.items.vtt.useCases.0'),
      t('subtitlePage.formatCards.items.vtt.useCases.1')
    ]
  },
  {
    name: 'ASS',
    title: t('subtitlePage.formatCards.items.ass.title'),
    alt: t('subtitlePage.formatCards.items.ass.alt'),
    icon: 'material-symbols:auto-awesome',
    image: '/images/subtitle/format-ass.jpeg',
    extensions: ['.ass', '.ssa'],
    features: [
      t('subtitlePage.formatCards.items.ass.features.0'),
      t('subtitlePage.formatCards.items.ass.features.1'),
      t('subtitlePage.formatCards.items.ass.features.2')
    ],
    useCases: [
      t('subtitlePage.formatCards.items.ass.useCases.0'),
      t('subtitlePage.formatCards.items.ass.useCases.1')
    ]
  }
]
</script>

<style scoped lang="scss">
.subtitle-format-cards {
  padding: $spacing-xxl 0;
}

.section-title {
  text-align: center;
  font-size: 2rem;
  font-weight: 700;
  color: $gray-900;
  margin-bottom: $spacing-xl;
}

.format-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: $spacing-xl;
  max-width: 1200px;
  margin: 0 auto;
}

.format-card {
  background: $white;
  border-radius: $border-radius-lg;
  box-shadow: $shadow-md;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: $shadow-lg;
  }
}

.card-header {
  padding: $spacing-lg;
  background: linear-gradient(135deg,
    rgba(#8B5CF6, 0.1) 0%,
    rgba(#4A90E2, 0.1) 100%);
  border-bottom: 2px solid rgba(#8B5CF6, 0.2);
  display: flex;
  align-items: center;
  gap: $spacing-md;

  .format-icon {
    color: #8B5CF6;
  }

  .format-name {
    font-size: 1.25rem;
    font-weight: 600;
    color: $gray-900;
    margin: 0;
  }
}

.format-image {
  width: 100%;
  height: 200px;
  overflow: hidden;
  background: $gray-100;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.05);
  }
}

.format-details {
  padding: $spacing-lg;
  display: flex;
  flex-direction: column;
  gap: $spacing-md;
}

.detail-item {
  display: flex;
  gap: $spacing-sm;
  font-size: 0.9rem;
  line-height: 1.6;

  .detail-label {
    font-weight: 600;
    color: $gray-700;
    flex-shrink: 0;
  }

  .detail-value {
    color: $gray-600;
  }
}

@media (max-width: 768px) {
  .format-grid {
    grid-template-columns: 1fr;
    gap: $spacing-lg;
  }

  .section-title {
    font-size: 1.75rem;
  }
}
</style>
